"use client";
import Fabs from "@/components/Fabs";
import FriendList from "@/components/FriendList";
import GoogleButton from "@/components/GoogleButton";
import { LayoutPanelBorder } from "@/components/Layout";
import SearchFooter from "@/components/SearchFooter";
import SetLists from "@/components/SetLists";
import SongList from "@/components/SongList";
import { ServerContext } from "@/context/Server";
import { useApp, useAppFile, useAppStore } from "@/store/app";
//import { useReplicacheFriendIdMap } from "@/store/replicache";
import { TABS } from "@/utils/constants";
import useInfo from "@/utils/info";
import type { TabValue } from "@/utils/types";
import cx from "classnames";
import { type MouseEvent, useContext, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import shallow from "zustand/shallow";

const LOG_TAG = "SongListContainer";

export default function SongListContainer() {
  //const { friendIdMap } = useReplicacheFriendIdMap();
  const { initializedState, isSearchOpen, selectedTab, setSelectedTab, user } =
    useAppStore(
      ({
        initializedState,
        isSearchOpen,
        selectedTab,
        setSelectedTab,
        user,
      }) => ({
        initializedState,
        isSearchOpen,
        selectedTab,
        setSelectedTab,
        user,
      }),
      shallow,
    );

  const { session } = useContext(ServerContext);
  const [showSignInBanner, setShowSignInBanner] = useState(!user && !session);
  const { handleDrop } = useAppFile();
  const {
    isLayoutPhone,
    isRouteHome,
    isRouteSongEditor,
    isRouteSetList,
    isRouteSongView,
    songListWidth,
  } = useInfo();

  const tabs = TABS;

  const { getRootProps } = useDropzone({
    onDrop: handleDrop,
    noClick: true,
  });

  const handleTabChange = (_event: MouseEvent, value: TabValue) => {
    console.debug("🔤 SongListContainer handleTabChange", value);
    window.analyticsLogEvent("Clicked Tab", { tab: value });
    setSelectedTab(value);
  };

  useEffect(() => {
    if (!initializedState) return;

    if (!selectedTab) {
      setSelectedTab("songlist");
      return;
      //setSelectedTab(isLayoutPhone ? "welcome" : "songlist");
    }

    // fix old clients
    // @ts-ignore
    if (selectedTab === "abc" || selectedTab === "recent") {
      setSelectedTab("songlist");
      return;
    }
    // @ts-ignore
    if (!isLayoutPhone && selectedTab === "welcome") {
      setSelectedTab("songlist");
    }
  }, [initializedState, isLayoutPhone, setSelectedTab, selectedTab]);

  useEffect(() => {
    setShowSignInBanner(!user && !session);
  }, [session, user]);

  // useEffect(() => {
  //   if (!friendIdMap) return;
  //   /*console.debug("SongListContainer friendIdMap useEffect", {
  //     friendIdMap,
  //   });*/

  //   let badgeCount = 0;
  //   const friendIds = Object.keys(friendIdMap);
  //   for (const friendId of friendIds) {
  //     const mapMeta = friendIdMap[friendId];
  //     if (mapMeta.last_viewed_timestamp === 0) {
  //       badgeCount++;
  //     }
  //   }
  // }, [friendIdMap]);

  /*
  console.debug(LOG_TAG, {
    isLayoutPhone,
    songListWidth,
    isSearchOpen,
    initializedState,
    selectedTab,
  });
  */

  // Don't love that we have to do this.
  // There must be some way to get at the URL path in a layout component...
  if (isRouteSongEditor) return null;
  //console.debug(LOG_TAG, { isLayoutPhone, isRouteHome, isRouteSetList });
  if (isLayoutPhone && !(isRouteHome || (isRouteSetList && !isRouteSongView)))
    return null;

  return (
    <div
      className={cx("flex flex-col h-full relative bg-base-200 print:hidden", {
        "w-full": isLayoutPhone,
      })}
      {...getRootProps()}
      style={{
        width: isLayoutPhone ? undefined : songListWidth,
        cursor: "auto",
      }}
    >
      <LayoutPanelBorder />
      {!isSearchOpen && <Fabs />}
      <div className="flex-1 overflow-auto">
        {!isSearchOpen && (
          <div className="tabs w-full bg-base-300 h-12 items-center">
            {tabs.map(({ label, value }) => (
              <div
                key={value}
                className={cx(
                  "tab tab-bordered h-full capitalize text-medium text-white flex-1",
                  selectedTab === value ? "tab-active" : "opacity-60",
                )}
                onClick={(e) => handleTabChange(e, value)}
              >
                {label}
              </div>
            ))}
          </div>
        )}

        {isSearchOpen ? (
          <>
            <SongList />
            {!!user && <SearchFooter />}
          </>
        ) : (
          <>
            {selectedTab === "songlist" ? (
              <>
                {showSignInBanner && <SignInBanner />}
                <SongList />
              </>
            ) : selectedTab === "setlists" ? (
              <>
                {showSignInBanner && <SignInBanner />}
                <SetLists />
              </>
            ) : (
              <>
                {showSignInBanner && <SignInBanner />}
                <FriendList />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const SignInBanner = () => {
  const { signIn } = useApp();
  const { isLayoutPhone } = useInfo();
  return (
    <div className="bg-primary p-4 text-primary-content">
      {/* biome-ignore lint/a11y/useValidAnchor: <explanation> */}
      <a className="underline cursor-pointer" onClick={signIn}>
        Sign in
      </a>{" "}
      to create your own Songbook
      {isLayoutPhone && (
        <div className="mt-2.5 flex justify-center">
          <GoogleButton onClick={signIn} />
        </div>
      )}
    </div>
  );
};
